import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth'
import { auth } from '../services/firebase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [authLogs, setAuthLogs] = useState([])
  const [redirectCheckComplete, setRedirectCheckComplete] = useState(false)

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toISOString()
    const log = { id: Date.now(), timestamp, message, type }
    setAuthLogs(prev => [log, ...prev].slice(0, 50)) // Keep last 50 logs
    console.log(`[Auth ${type.toUpperCase()}] ${timestamp}: ${message}`)
  }

  // Google Sign In with Popup (recommended for local development)
  const loginWithGoogle = async (useRedirect = false) => {
    try {
      setError(null)
      setLoading(true)
      
      const provider = new GoogleAuthProvider()
      // Optional: Add additional scopes
      provider.addScope('profile')
      provider.addScope('email')

      if (useRedirect) {
        addLog('Initiating Google sign in with redirect')
        await signInWithRedirect(auth, provider)
        // Note: redirect will reload the page, so code after this won't execute
      } else {
        addLog('Initiating Google sign in with popup')
        const result = await signInWithPopup(auth, provider)
        addLog(`Sign in successful: ${result.user.email}`)
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to sign in with Google'
      setError(errorMessage)
      addLog(errorMessage, 'error')
      console.error('Google sign in error:', err)
    } finally {
      if (!useRedirect) {
        setLoading(false)
      }
    }
  }

  // Email/Password Sign Up
  const signupWithEmail = async (email, password) => {
    try {
      setError(null)
      setLoading(true)
      addLog(`Attempting to sign up with email: ${email}`)

      const result = await createUserWithEmailAndPassword(auth, email, password)
      addLog(`Successfully signed up user: ${result.user.email}`)
      return result
    } catch (err) {
      const errorMessage = err.message || 'Failed to sign up'
      setError(errorMessage)
      addLog(errorMessage, 'error')
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Email/Password Sign In
  const loginWithEmail = async (email, password) => {
    try {
      setError(null)
      setLoading(true)
      addLog(`Attempting to sign in with email: ${email}`)

      const result = await signInWithEmailAndPassword(auth, email, password)
      addLog(`Successfully signed in user: ${result.user.email}`)
      return result
    } catch (err) {
      const errorMessage = err.message || 'Failed to sign in'
      setError(errorMessage)
      addLog(errorMessage, 'error')
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Sign Out
  const logout = async () => {
    try {
      setError(null)
      addLog('Signing out user')
      await signOut(auth)
      addLog('Successfully signed out')
    } catch (err) {
      const errorMessage = err.message || 'Failed to sign out'
      setError(errorMessage)
      addLog(errorMessage, 'error')
      console.error('Sign out error:', err)
    }
  }

  // Clear logs
  const clearLogs = () => {
    setAuthLogs([])
    addLog('Auth logs cleared')
  }

  // Handle redirect result on page load
  useEffect(() => {
    let isMounted = true
    
    const handleRedirectResult = async () => {
      try {
        addLog('Checking for redirect result')
        const result = await getRedirectResult(auth)
        
        if (!isMounted) return
        
        if (result) {
          addLog(`Redirect sign in successful: ${result.user.email}`)
          addLog(`User UID: ${result.user.uid}`)
        } else {
          addLog('No redirect result (normal page load)', 'info')
        }
      } catch (err) {
        if (!isMounted) return
        
        const errorMessage = err.message || 'Failed to handle redirect result'
        setError(errorMessage)
        addLog(`Redirect error: ${errorMessage}`, 'error')
        console.error('Redirect result error:', err)
        
        if (err.code) {
          addLog(`Error code: ${err.code}`, 'error')
        }
      } finally {
        if (isMounted) {
          setRedirectCheckComplete(true)
          addLog('Redirect check complete')
        }
      }
    }

    handleRedirectResult()
    
    return () => {
      isMounted = false
    }
  }, [])

  // Listen for authentication state changes
  useEffect(() => {
    // Don't set up listener until redirect check is done
    if (!redirectCheckComplete) {
      return
    }
    
    addLog('Setting up auth state listener')
    let isFirstLoad = true

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          emailVerified: user.emailVerified,
          providerId: user.providerId,
          providerData: user.providerData
        })
        addLog(`Auth state changed: User logged in - ${user.email}`)
      } else {
        setCurrentUser(null)
        if (isFirstLoad) {
          addLog('Auth state changed: No user on initial load')
        } else {
          addLog('Auth state changed: User logged out')
        }
      }
      
      setLoading(false)
      isFirstLoad = false
    })

    return () => {
      unsubscribe()
      addLog('Auth state listener cleaned up')
    }
  }, [redirectCheckComplete])

  const value = {
    currentUser,
    loading,
    error,
    authLogs,
    loginWithGoogle,
    signupWithEmail,
    loginWithEmail,
    logout,
    clearLogs,
    addLog
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}